import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  startOfDay,
  endOfDay,
  subMonths,
  startOfYear,
  endOfYear,
  subYears,
} from "date-fns";
import { CalendarIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { type DateRange } from "react-day-picker";
import { PopoverTriggerButton } from "../PopoverTriggerButton";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils/common";
import { toDisplayDate } from "@/lib/utils/dates";

export type DateRangePickerProps = {
  id?: string;
  dateRange: DateRange;
  onChange: (date: DateRange | undefined) => void;
  className?: string;
  isModal?: boolean;
};

type DateRangePickerContentProps = {
  dateRange: DateRange;
  selectedPreset: PresetId | undefined;
  month: Date | undefined;
  onMonthChange: (date: Date | undefined) => void;
  onPresetSelect: (preset: PredefinedRange) => void;
  onCalendarSelect: (range: DateRange | undefined) => void;
  onClear: (e: React.MouseEvent) => void;
};

function MobileDateRangeContent({
  dateRange,
  selectedPreset,
  month,
  onMonthChange,
  onPresetSelect,
  onCalendarSelect,
  onClear,
}: DateRangePickerContentProps) {
  return (
    <div className="flex flex-col">
      <div className="p-4 pb-3 border-b border-border">
        <div className="grid grid-cols-3 gap-2">
          {predefinedRanges.map((range) => (
            <Button
              key={range.id}
              variant={selectedPreset === range.id ? "default" : "ghost"}
              size="sm"
              className="text-xs px-2 py-2 h-auto min-h-9"
              onClick={() => onPresetSelect(range)}
            >
              {range.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="px-2 pb-2">
        <Calendar
          initialFocus
          mode="range"
          selected={dateRange}
          onSelect={onCalendarSelect}
          defaultMonth={dateRange?.from}
          onMonthChange={onMonthChange}
          month={month}
          numberOfMonths={1}
          className="w-full"
        />
        {dateRange?.from ? (
          <div className="flex justify-end pt-2 pr-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={onClear}>
              <XIcon className="h-3 w-3" />
              Clear
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DesktopDateRangeContent({
  dateRange,
  selectedPreset,
  month,
  onMonthChange,
  onPresetSelect,
  onCalendarSelect,
  onClear,
}: DateRangePickerContentProps) {
  return (
    <div className="flex">
      <div className="border-r border-border p-2 space-y-2 w-min">
        {predefinedRanges.map((range) => (
          <Button
            key={range.id}
            variant={selectedPreset === range.id ? "default" : "ghost"}
            className="w-full justify-start text-left"
            onClick={() => onPresetSelect(range)}
          >
            {range.label}
          </Button>
        ))}
      </div>
      <div className="flex flex-col items-end justify-between">
        <Calendar
          initialFocus
          mode="range"
          selected={dateRange}
          onSelect={onCalendarSelect}
          defaultMonth={dateRange?.from}
          onMonthChange={onMonthChange}
          month={month}
          numberOfMonths={2}
        />
        {dateRange?.from ? (
          <Button variant="outline" className="m-3 rounded-3xl" onClick={onClear}>
            <XIcon className="h-4" />
            Clear
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function DateRangePicker({ className, onChange, dateRange, id, isModal }: DateRangePickerProps) {
  const [month, setMonth] = useState<Date | undefined>(dateRange?.from);
  const [selectedPreset, setSelectedPreset] = useState<PresetId>();
  const isMobile = useIsMobile();

  const formatDateRange = (range: DateRange) => {
    if (!range.from) return "";
    if (!range.to) return toDisplayDate(range.from);
    return `${toDisplayDate(range.from)} - ${toDisplayDate(range.to)}`;
  };

  const handlePredefinedRange = (preset: PredefinedRange) => {
    if (selectedPreset === preset.id) {
      setSelectedPreset(undefined);
      onChange(undefined);
      return;
    }

    const range = preset.getValue();
    setSelectedPreset(preset.id);
    setMonth(range.from);
    onChange(range);
  };

  const handleCalendarSelect = (range: DateRange | undefined) => {
    setSelectedPreset(undefined);
    onChange(range);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPreset(undefined);
    onChange(undefined);
  };

  const contentProps: DateRangePickerContentProps = {
    dateRange,
    selectedPreset,
    month,
    onMonthChange: setMonth,
    onPresetSelect: handlePredefinedRange,
    onCalendarSelect: handleCalendarSelect,
    onClear: handleClear,
  };

  return (
    <Popover modal={isModal}>
      <PopoverTrigger asChild>
        <PopoverTriggerButton id={id} className={className}>
          <span className="flex items-center gap-2">
            <CalendarIcon className={cn("h-4 w-4", dateRange.from ? "text-foreground" : "text-muted-foreground")} />
            <span className={cn("font-normal", dateRange.from ? "text-foreground" : "text-muted-foreground")}>
              {dateRange?.from ? formatDateRange(dateRange) : "Select date range"}
            </span>
          </span>
        </PopoverTriggerButton>
      </PopoverTrigger>
      <PopoverContent className={cn("w-auto p-0", isMobile && "w-[calc(100vw-1rem)] max-w-md")} align="start">
        {isMobile ? <MobileDateRangeContent {...contentProps} /> : <DesktopDateRangeContent {...contentProps} />}
      </PopoverContent>
    </Popover>
  );
}

type PredefinedRange = (typeof predefinedRanges)[number];
type PresetId = PredefinedRange["id"];

const predefinedRanges = [
  {
    id: "today",
    label: "Today",
    getValue: () => ({
      from: startOfDay(new Date()),
      to: endOfDay(new Date()),
    }),
  },
  {
    id: "last7Days",
    label: "Last 7 Days",
    getValue: () => ({
      from: startOfDay(subDays(new Date(), 6)),
      to: endOfDay(new Date()),
    }),
  },
  {
    id: "thisWeek",
    label: "This Week",
    getValue: () => ({
      from: startOfWeek(new Date(), { weekStartsOn: 1 }),
      to: endOfWeek(new Date(), { weekStartsOn: 1 }),
    }),
  },
  {
    id: "thisMonth",
    label: "This Month",
    getValue: () => ({
      from: startOfMonth(new Date()),
      to: endOfMonth(new Date()),
    }),
  },
  {
    id: "lastMonth",
    label: "Last Month",
    getValue: () => ({
      from: startOfMonth(subMonths(new Date(), 1)),
      to: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
  {
    id: "last3Months",
    label: "Last 3 Months",
    getValue: () => ({
      from: startOfDay(subMonths(new Date(), 3)),
      to: endOfDay(new Date()),
    }),
  },
  {
    id: "last6Months",
    label: "Last 6 Months",
    getValue: () => ({
      from: startOfDay(subMonths(new Date(), 6)),
      to: endOfDay(new Date()),
    }),
  },
  {
    id: "thisYear",
    label: "This Year",
    getValue: () => ({
      from: startOfYear(new Date()),
      to: endOfDay(new Date()),
    }),
  },
  {
    id: "lastYear",
    label: "Last Year",
    getValue: () => ({
      from: startOfYear(subYears(new Date(), 1)),
      to: endOfYear(subYears(new Date(), 1)),
    }),
  },
] as const;
